import React, { useCallback, useMemo, memo } from 'react';
import { Table, Space, Card, Tooltip, Image } from 'antd';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { DeleteIcon, EditIcon } from 'icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';
import useHomepageBlocks from '../useHomepageBlocks';
import { Button } from 'components';
import { HomepageBlocksFilters } from './HomepageBlocksFilters';

export const HomepageBlocksList = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pages = useRecoilValue(getPage);

  const {
    homepageBlocks,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    shoppingCenters,
    loadingShoppingCenters,
    fetchHomepageBlocks,
    deleteHomepageBlock,
    handleTableChange,
    updateFilters,
  } = useHomepageBlocks();

  const handleDelete = useCallback(
    async (id) => {
      try {
        const success = await deleteHomepageBlock(id);
        if (success) {
          fetchHomepageBlocks();
        }
      } catch (err) {
        console.error('Error deleting homepage block:', err);
      }
    },
    [deleteHomepageBlock, fetchHomepageBlocks],
  );

  const handleFiltersChange = useCallback(
    (newFilters) => {
      updateFilters(newFilters);
    },
    [updateFilters],
  );

  const columns = useMemo(
    () => [
      {
        title: t('Immagine'),
        dataIndex: 'file',
        key: 'file',
        width: 100,
        render: (file) => {
          if (file?.url) {
            return (
              <Image
                src={file.url}
                alt='Homepage block'
                style={{ width: 60, height: 60, objectFit: 'cover' }}
                preview={false}
              />
            );
          }
          return '-';
        },
      },
      {
        title: t('Titolo'),
        dataIndex: 'title_it',
        key: 'title_it',
        render: (text) => text || '-',
      },
      {
        title: t('Descrizione'),
        dataIndex: 'description_it',
        key: 'description_it',
        render: (text) => {
          if (!text) return '-';
          return text.length > 100 ? `${text.substring(0, 100)}...` : text;
        },
      },
      {
        title: t('Link'),
        dataIndex: 'link_it',
        key: 'link_it',
        render: (text) => {
          if (!text) return '-';
          return (
            <a href={text} target='_blank' rel='noopener noreferrer'>
              {text.length > 30 ? `${text.substring(0, 30)}...` : text}
            </a>
          );
        },
      },
      {
        title: t('Shopping Center'),
        dataIndex: 'shopping_center',
        key: 'shopping_center',
        render: (shoppingCenter) => shoppingCenter?.title || '-',
      },
      {
        title: t('Azioni'),
        key: 'actions',
        width: 180,
        render: (_, record) => (
          <Space>
            {(pages?.ability?.can_read !== false ||
              record.can_read !== false) && (
              <Tooltip title={t('Visualizza')}>
                <Button
                  type='secondary'
                  icon={<EyeOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/website/homepage_blocks/${record.id}`);
                  }}
                />
              </Tooltip>
            )}
            {record.can_update !== false && (
              <Tooltip title={t('Modifica')}>
                <Button
                  type='secondary'
                  icon={<EditIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      `/website/homepage_blocks/edit/${record.id}`,
                    );
                  }}
                />
              </Tooltip>
            )}
            {record.can_destroy !== false && (
              <Tooltip title={t('Elimina')}>
                <Button
                  type='red'
                  icon={<DeleteIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(record.id);
                  }}
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, handleDelete, pages?.ability?.can_read],
  );

  const pagination = useMemo(
    () => ({
      current: currentPage,
      pageSize: pageSize,
      total: total,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total, range) =>
        t('Paginazione elementi', { start: range[0], end: range[1], total }),
    }),
    [currentPage, pageSize, total, t],
  );

  return (
    <Card
      title={
        <span style={{ fontSize: '24px', fontWeight: '600' }}>
          {t('In evidenza')}
        </span>
      }
      extra={
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={() => navigate('/website/homepage_blocks/create')}
          style={{ marginBottom: 12, width: '140px', height: 48, backgroundColor: '#002637', borderColor: '#002637' }}
        >
          {t('Nuovo')}
        </Button>
      }
    >
      <HomepageBlocksFilters
        onFiltersChange={handleFiltersChange}
        shoppingCenters={shoppingCenters}
        loadingShoppingCenters={loadingShoppingCenters}
        filters={filters}
      />
      
      <div>
        {!!homepageBlocks?.length && (
          <Table
            columns={columns}
            dataSource={homepageBlocks}
            pagination={pagination}
            onChange={handleTableChange}
            loading={loading}
            rowKey='id'
            onRow={(record) => ({
              onClick: () => {
                if (record?.id) {
                  navigate(`/website/homepage_blocks/${record.id}`);
                }
              },
              style: { cursor: 'pointer' },
            })}
          />
        )}
        {!homepageBlocks?.length && !loading && (
          <Card>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p>{t('Nessun homepage block trovato')}</p>
            </div>
          </Card>
        )}
      </div>
    </Card>
  );
});

export default HomepageBlocksList;
